import { ChangeDetectionStrategy, Component, input, OnDestroy, OnInit, ViewChild, DestroyRef, inject, effect, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { take } from 'rxjs/operators';
import { Task } from '../../../../rent-module/shared/entities/task/task';
import { EditDateTimePeriodInputV2Component } from '../../../../core/components/edit-dueDateTime-form-control-v2/edit-dueDateTime-form-control-v2.component';
import { WidgetCoordinatorService } from '../../../../core/services/issue/widget-coordinator.service';
import { CommandInput, CommandTemplate } from '../../../../core/entities/commandTemplate/commandTemplate';
import { CommandService } from '../../../../core/services/command/command.service';
import { RefetchCoordinator } from '../../../../core/services/refetch-coordinator/refetch-coordinator.service';
import { TaskInput } from 'src/app/rent-module/shared/entities/taskInput/taskInput';
import { TaskWidget } from '../../interfaces/task-widget.interface';
import { TasksService } from 'src/app/rent-module/shared/services/tasks/tasks.service';
import { DateTimePeriod } from 'src/app/rent-module/shared/entities/dateTimePeriod/dateTimePeriod';

@Component({
  selector: 'nx-task-dueDateTime-widget',
  templateUrl: './dueDateTime-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskDueDateTimeWidgetComponent implements OnDestroy, OnInit, TaskWidget {

  readonly task = input.required<Task>();
  @ViewChild('dateTimeComponent') dateTimeComponent: EditDateTimePeriodInputV2Component;

  readonly editMode = signal(false);
  readonly isLoading = signal(false);
  readonly commandTemplate = signal<CommandTemplate | null>(null);

  readonly dueDateTimeControl = new FormControl<DateTimePeriod | null>(null);
  readonly form = new FormGroup({
    dueDateTime: this.dueDateTimeControl
  });

  private readonly widgetId = `dueDateTime-widget-${Math.random().toString(36).substr(2, 9)}`;
  private unregisterRefetch?: () => void;

  // Injected services
  private readonly destroyRef = inject(DestroyRef);
  private readonly commandService = inject(CommandService);
  private readonly coordinator = inject(WidgetCoordinatorService);
  private readonly refetchCoordinator = inject(RefetchCoordinator);
  readonly tasksService = inject(TasksService);

  constructor() {
    // React to task changes
    effect(() => {
      const task = this.task();
      if (task?.id) {
        this.loadCommandTemaplate();
      }
    });
  }

  ngOnInit(): void {
    // Register with RefetchCoordinator as detail view
    const task = this.task();
    if (task?.id) {
      this.unregisterRefetch = this.refetchCoordinator.register(
        this,
        task.getEntityType(),
        task.id
      );
    }

    // Subscribe to cancel all requests
    this.coordinator.cancelAll$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(exceptId => {
        if (exceptId !== this.widgetId) {
          this.cancel();
        }
      });
  }

  loadCommandTemaplate() {
    const commandInput = new CommandInput();
    commandInput.name = "taskSetDueDateTimePeriodCommand";
    commandInput.addParameter('commandProviderId', this.task().id);
      
    this.commandService.getCommandByCommandInput(commandInput)
        .pipe(take(1))
        .subscribe(result => {
          this.commandTemplate.set(result.data.getCommandByCommandInput);
          this.isLoading.set(false);
        });
  }

  ngOnDestroy(): void {
    this.unregisterRefetch?.();
  }

  edit() {
    this.coordinator.requestCancelAll(this.widgetId);
    this.editMode.set(true);
  }

  save() {
    this.editMode.set(false);
    const task = this.task();
    const taskInput = new TaskInput();
    taskInput.id = task?.id;
    taskInput.dueDateTimePeriod = this.form.value?.dueDateTime;
    const commandInput = new CommandInput();
    commandInput.name = this.commandTemplate()?.name;
    commandInput.addParameter('commandProviderId', task.id);
    commandInput.addParameter('taskInput', taskInput);
    this.isLoading.set(true);
    this.commandService.sendCommand(commandInput, (result) => {
      this.loadCommandTemaplate();
      this.refetchCoordinator.emit({ entityType: task.getEntityType(), entityId: task.id });
    });
  }

  cancel() {
    this.editMode.set(false);
  }

  reload(): void {
    this.loadCommandTemaplate();
  }

}
